/* eslint-disable arrow-body-style */
import React, { useState, useEffect, useRef } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Card, CardBody, CardHeader, Button, Alert,
  InputGroup,
} from 'reactstrap';
import {
  Table, Tbody, Thead, Tr, Td,
} from 'react-super-responsive-table';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import {
  Formik, Field, Form, ErrorMessage,
} from 'formik';
import * as Yup from 'yup';

import { Can } from '../../utilities/Can';
import { get, post, del } from '../../helpers/api_helper';
import ProductQueryCard from '../../components/BigCommerce/ProductQueryCard';
import WebhookModal from '../../components/BigCommerce/WebhookModal';

function BigCommerce(props) {
  const { t } = props;

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const saleId = useRef(null);

  const [storeInfo, setStoreInfo] = useState(null);
  const [trees, setTrees] = useState([]);
  const [localTrees, setLocalTrees] = useState([]);
  const [localTree, setLocalTree] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerError, setTriggerError] = useState(null);
  const [triggerSingleLoading, setTriggerSingleLoading] = useState(false);
  const [triggerSingleError, setTriggerSingleError] = useState(null);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [showWebhooks, setShowWebhooks] = useState(false);
  const [webhooks, setWebhooks] = useState([]);

  const getInfo = async () => {
    try {
      const info = await get('/api/bc/info');

      setStoreInfo(info);
    } catch (err) {
      toastr.error(props.t('Could not get store info'));
    }
  };

  const getLocalTrees = async () => {
    try {
      const lt = await get('/api/listings/trees?q=BigCommerce');

      setLocalTrees(lt.results);
    } catch (err) {
      toastr.error(props.t('Could not get local trees'));
    }
  };

  const getLocalTreeId = async () => {
    try {
      const setting = await get('/api/settings/bc_local_tree_id');

      setLocalTree(parseInt(setting, 10));
    } catch (err) {
      toastr.error(props.t('Could not get local trees'));
    }
  };

  const setLocalTreeId = async (id) => {
    if (id === '0') return;

    try {
      await post('/api/settings', {
        k: 'bc_local_tree_id',
        v: id,
      });

      toastr.success(props.t('Local tree set successfully'));
    } catch (err) {
      toastr.error(props.t('Could not set local tree'));
    }
  };

  const getTrees = async () => {
    try {
      const remoteTrees = await get('/api/bc/trees');

      setTrees(remoteTrees);
    } catch (err) {
      toastr.error(props.t('Could not get store trees'));
    }
  };

  const createTree = async (values) => {
    try {
      setCloning(true);
      await post(`/api/bc/tree/${values.tree_id}/clone/${values.new_tree}`);
      toastr.success(props.t('Tree successfully cloned'));
    } catch (err) {
      toastr.error(props.t('Could not clone tree'));
    } finally {
      setCloning(false);
    }
  };

  const download = async () => {
    try {
      setTriggerLoading(true);
      await get('/api/bc/sales');
    } catch (error) {
      setTriggerError(error.message);
    } finally {
      setTriggerLoading(false);
    }
  };

  const downloadSingle = async () => {
    try {
      setTriggerSingleLoading(true);
      const id = saleId.current.value;
      if (id === '') return;

      await get(`/api/bc/sale/${id}`);
    } catch (error) {
      setTriggerSingleError(error.message);
    } finally {
      setTriggerSingleLoading(false);
    }
  };

  const getWebhooks = async () => {
    try {
      const wh = await get('/api/bc/webhooks');

      setWebhooks(wh);
      setShowWebhooks(true);
    } catch (error) {
      toastr.error(error.message);
    }
  };

  const deleteWebhook = async (id) => {
    try {
      await del(`/api/bc/webhook/${id}`);
      toastr.success('Webhook deleted');
      getWebhooks();
    } catch (error) {
      toastr.error(error.message);
    }
  };

  const createWebhook = async () => {
    setWebhookModalOpen(true);
  };

  useEffect(() => {
    getInfo();
    getTrees();
    getLocalTrees();
    getLocalTreeId();
  }, []);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xxl="5">
            <Card>
              <CardHeader className="bg-transparent border-bottom">{t('BigCommerce Utilities')}</CardHeader>
              <CardBody>
                <Can i="see" this="bc">
                  <Table className="table table-striped table-bordered">
                    <Tbody>
                      <Tr>
                        <Td>{props.t('Store Hash')}</Td>
                        <Td>
                          {storeInfo !== null && (
                            <span>{storeInfo.id}</span>
                          )}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td>{props.t('Store URL')}</Td>
                        <Td>
                          {storeInfo !== null && (
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={storeInfo.secure_url}
                            >
                              {storeInfo.secure_url}
                            </a>
                          )}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td>{props.t('Control Panel')}</Td>
                        <Td>
                          {storeInfo !== null && (
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={`${storeInfo.control_panel_base_url}/manage/dashboard`}
                            >
                              {`${storeInfo.control_panel_base_url}/manage/dashboard`}
                            </a>
                          )}
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Can>

                <Can not i="see" this="bc"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="bg-transparent border-bottom">{t('Triggers')}</CardHeader>
              <CardBody>
                <div className="hstack gap-4">
                  <div>
                    {triggerError !== null && (
                      <Alert color="error">{triggerError}</Alert>
                    )}
                    <Button color="light" onClick={download} disabled={triggerLoading}>{props.t('Download Sales')}</Button>
                  </div>

                  <div>
                    {triggerSingleError !== null && (
                      <Alert color="error">{triggerSingleError}</Alert>
                    )}
                    <InputGroup>
                      <input type="number" ref={saleId} className="form-control" />
                      <Button color="light" onClick={downloadSingle} disabled={triggerSingleLoading}>{props.t('Download Sale')}</Button>
                    </InputGroup>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="bg-transparent border-bottom">{t('Clone Remote Category Tree Locally')}</CardHeader>
              <CardBody>
                <Formik
                  enableReinitialize
                  initialValues={{ }}
                  validationSchema={Yup.object().shape({
                    tree_id: Yup.string().required(t('Remote tree is required')),
                    new_tree: Yup.string().required(t('New tree name is required')),
                  })}
                  onSubmit={createTree}
                >
                  {({
                    // eslint-disable-next-line no-unused-vars
                    touched, errors, values, setFieldValue,
                  }) => (
                    <Form autoComplete="off">
                      <Row>
                        <Col md="4">
                          <label htmlFor="tree">{props.t('BigCommerce tree')}</label>
                        </Col>
                        <Col md="4">
                          <label htmlFor="ltree">{props.t('New local tree')}</label>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="4">
                          <Field
                            className="form-control"
                            name="tree_id"
                            id="tree"
                            as="select"
                            onChange={(e) => {
                              setFieldValue('tree_id', e.target.value);
                            }}
                          >
                            {trees.length === 0 && (
                              <option value="0">{t('Loading...')}</option>
                            )}
                            {trees.length > 0 && (
                              <option>{t('Select a tree...')}</option>
                            )}
                            {trees.length > 0 && trees.map((tree) => (
                              <option key={tree.id} value={tree.id}>{tree.name}</option>
                            ))}
                          </Field>
                          <ErrorMessage name="tree_id" component="div" className="text-danger" />
                        </Col>
                        <Col md="4">
                          <Field
                            name="new_tree"
                            id="ltree"
                            className={`form-control ${(errors.new_tree && touched.new_tree ? 'is-invalid' : '')}`}
                          />
                          <ErrorMessage name="new_tree" component="div" className="invalid-feedback" />
                        </Col>
                        <Col md="4">
                          {cloning && (
                            <Button color="light" disabled>
                              <span className="mdi mdi-loading mdi-spin me-2" />
                              {props.t('Cloning...')}
                            </Button>
                          )}
                          {!cloning && (
                            <Button color="light" type="submit">{props.t('Clone')}</Button>
                          )}
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>

          <Col xxl="6">
            <Card>
              <CardHeader className="bg-transparent border-bottom">{t('Active Category Tree')}</CardHeader>
              <CardBody>
                <Can i="see" this="bc">
                  <div>
                    {localTree !== null && (
                    <select className="form-control" name="tree" onChange={(e) => { setLocalTreeId(e.target.value); }}>
                      <option value="0">{t('Select a tree...')}</option>
                      {localTrees.length > 0 && localTrees.map((tree) => (
                        <option key={tree.id} value={tree.id} selected={tree.id === localTree}>{`${tree.key}: ${tree.description}`}</option>
                      ))}
                    </select>
                    )}
                  </div>
                </Can>

                <Can not i="see" this="bc"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="bg-transparent border-bottom">{t('Webhooks')}</CardHeader>
              <CardBody>
                <Can i="see" this="bc">
                  <div className="hstack gap-3">
                    <Button color="light" size="sm" onClick={getWebhooks}>{props.t('Load Webhooks')}</Button>
                    <Button color="primary" className="btn-soft-primary" size="sm" onClick={createWebhook}>{props.t('Create Webhook')}</Button>
                  </div>

                  {showWebhooks && (
                    <div className="table-responsive">
                      <Table className="table table-striped table-bordered mt-3">
                        <Thead>
                          <Tr>
                            <Td>{props.t('Scope')}</Td>
                            <Td>{props.t('Destination')}</Td>
                            <Td>{props.t('Active')}</Td>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {webhooks.data.length > 0 && webhooks.data.map((wh) => (
                            <Tr key={wh.id}>
                              <Td className="align-middle">{wh.scope}</Td>
                              <Td className="align-middle">{wh.destination}</Td>
                              <Td className="align-middle">{wh.is_active ? 'yes' : 'no' }</Td>
                              <Td className="align-middle">
                                <Button size="sm" color="danger" className="btn-soft-danger" onClick={() => { deleteWebhook(wh.id); }}>
                                  <span className="mdi mdi-close-thick" />
                                </Button>
                              </Td>
                            </Tr>
                          ))}

                          {webhooks.data.length === 0 && (
                            <Tr>
                              <Td colSpan="2">{props.t('No webhooks found')}</Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </div>
                  )}
                </Can>

                <Can not i="see" this="bc"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
            <ProductQueryCard />
          </Col>
        </Row>

        <WebhookModal
          shown={webhookModalOpen}
          onSuccess={() => { getWebhooks(); setWebhookModalOpen(false); }}
          onCancel={() => { setWebhookModalOpen(false); }}
        />
      </div>
    </div>
  );
}

BigCommerce.defaultProps = {
  t: (x) => (x),
};

BigCommerce.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(BigCommerce));
